/* 시즌 자동 선택 훅

   기존에는 LectureManager(2곳) / AttendanceManager / CareReportManager / VocaManager
   다섯 군데에 같은 로직이 복사되어 있었고, 조금씩 달라져 있었습니다.

   특히 '현재도 미래도 없을 때 어떤 지난 시즌을 고르는가'가 갈렸습니다.
     - LectureManager : 종료일이 가장 최근인 시즌   ← 의도에 맞는 동작
     - 나머지         : 시작일이 가장 늦은 시즌
   시즌 기간이 겹치거나 길이가 다르면 서로 다른 시즌이 선택되어,
   같은 날 같은 학원인데 화면마다 다른 시즌을 보여줄 수 있었습니다.

   여기서는 '종료일이 가장 최근인 시즌'으로 통일했습니다.
   방금 끝난 시즌을 보여주는 것이 사용자가 기대하는 동작이기 때문입니다.
*/

import { useState, useEffect, useCallback } from 'react';

/** 오늘 날짜를 기준으로 보여줄 시즌 ID를 고릅니다. (순수 함수 — 테스트/재사용 가능) */
export const pickSeasonForToday = (seasons, fallbackId = 'all') => {
  const list = Array.isArray(seasons) ? seasons.filter(s => s && s.id) : [];
  if (list.length === 0) return fallbackId;

  const todayStr = new Date().toISOString().split('T')[0];

  // 1) 오늘이 포함된 시즌
  const current = list.find(s => todayStr >= (s.startDate || '') && todayStr <= (s.endDate || ''));
  if (current) return current.id;

  // 2) 가장 가까운 미래 시즌
  const future = list
    .filter(s => (s.startDate || '') > todayStr)
    .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
  if (future.length > 0) return future[0].id;

  // 3) 가장 최근에 끝난 시즌
  const past = list
    .filter(s => (s.endDate || '') < todayStr)
    .sort((a, b) => String(b.endDate).localeCompare(String(a.endDate)));
  if (past.length > 0) return past[0].id;

  return fallbackId;
};

/**
 * 오늘에 해당하는 시즌을 자동 선택합니다.
 * 사용자가 직접 고른 뒤에는 그 값을 그대로 유지합니다.
 *
 * @param seasons      원본 시즌 배열 (masterData.seasons). 'all'/'legacy' 같은 가상 항목은 넣지 마세요.
 * @param loadingData  전역 데이터 로딩 여부
 * @param fallbackId   고를 시즌이 없을 때 사용할 값 ('all' 또는 'legacy')
 */
export const useSeasonAutoSelect = (seasons, loadingData, fallbackId = 'all') => {
  const [selectedSeasonId, setSelected] = useState('');
  const [isSeasonAutoSet, setIsSeasonAutoSet] = useState(false);
  const [userPicked, setUserPicked] = useState(false);
  const hasSeasons = Array.isArray(seasons) && seasons.some(s => s && s.id);

  /* ⚠ loadingData 만 믿으면 안 된다. DataContext 는 구독을 '걸자마자' loadingData 를 false 로 내려서,
     그 순간 시즌 목록은 아직 빈 배열이다. 예전에는 그 빈 목록으로 딱 한 번 고르고 끝나서
     새로고침·주소로 바로 연 화면은 늘 fallback('과거/미지정')에 머물렀다(11-02 발견 —
     학부모 출결 상황은 조회조차 안 했다).
     이제는 사용자가 직접 고르기 전까지, 시즌 목록이 바뀔 때마다(=도착할 때) 다시 고른다. */
  useEffect(() => {
    if (userPicked || loadingData) return undefined;
    setSelected(pickSeasonForToday(seasons, fallbackId));
    if (hasSeasons) { setIsSeasonAutoSet(true); return undefined; }
    // 시즌이 하나도 없는 학원이면 목록이 영영 오지 않는다 — 이 값을 기다리며 스피너를 띄우는 화면이 있으니
    // 3초 뒤 fallback 으로 화면을 연다(늦게 도착하면 위 규칙대로 다시 고른다).
    const t = setTimeout(() => setIsSeasonAutoSet(true), 3000);
    return () => clearTimeout(t);
  }, [seasons, hasSeasons, loadingData, userPicked, fallbackId]);

  const setSelectedSeasonId = useCallback((v) => { setUserPicked(true); setSelected(v); }, []);

  return { selectedSeasonId, setSelectedSeasonId, isSeasonAutoSet };
};
